import Breadcrumbs from 'Components/Breadcrumbs'
import CustomButton from 'Components/CustomButton'
import DeleteButton from 'Components/DeleteButton'
import DrawerRight from 'Components/DrawerRight'
import ListActions from 'Components/ListActions'
import { WithLoader } from 'Components/WithLoader'
import RefundCodeDetails from 'Services/ControlDataManagement/Features/RefundCode/Components/RefundCodeDetails'
import RefundCodeList from 'Services/ControlDataManagement/Features/RefundCode/Components/RefundCodeList'
import { useRefundCodesWithParams } from 'Services/ControlDataManagement/Features/RefundCode/Hooks'
import {
  useDeleteSingleRefundCodeMutation,
  useGetSingleRefundCodeQuery
} from 'Services/ControlDataManagement/Features/RefundCode/refundCodeApi'
import { RefundCodeTabType } from 'Services/ControlDataManagement/Features/RefundCode/types'
import {
  CONTROL_DATA_MANAGEMENT_ROOT_URL,
  ROWS_PER_PAGE_DEFAULT,
  SORT_ORDER_ASC,
  SORT_ORDER_DESC
} from 'Util/constants'
import { useConfirm } from 'Util/hooks/useConfirm'
import SnackbarUtils from 'Util/Snackbar'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import { TabContext, TabList, TabPanel } from '@mui/lab'
import { Box, Divider, Tab, Typography } from '@mui/material'
import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { t } from 'ttag'

const REFUND_CODE_DETAILS_DRAWER_TAB_PARAM_KEY = 'refundCodeDetailsDrawerTab'

export default function RefundCodeListPage() {
  const baseUrl = `${CONTROL_DATA_MANAGEMENT_ROOT_URL}/refund-codes`

  const navigate = useNavigate()
  const { confirm } = useConfirm()

  const {
    refundCodes,
    isFetching: isRefundCodesFetching,
    params,
    setParams,
    totalResults,
    selectedRefundCodeId,
    setSelectedRefundCodeId
  } = useRefundCodesWithParams()
  const { data: singleRefundCode, isFetching: isSingleRefundCodeFetching } =
    useGetSingleRefundCodeQuery(selectedRefundCodeId ?? '', {
      skip: !selectedRefundCodeId
    })
  const [deleteSingleRefundCode, { isLoading: isDeleteLoading }] =
    useDeleteSingleRefundCodeMutation()

  const [searchParams, setSearchParams] = useSearchParams()
  const refundCodeDetailsDrawerTabFromParams =
    searchParams.get(REFUND_CODE_DETAILS_DRAWER_TAB_PARAM_KEY) ?? RefundCodeTabType.DETAILS

  const [isDrawerOpen, setIsDrawerOpen] = useState(!!selectedRefundCodeId)

  const breadcrumbs = [
    {
      title: t`Home`,
      link: CONTROL_DATA_MANAGEMENT_ROOT_URL
    },
    {
      title: t`Refund codes`
    }
  ]

  const handleChangePage = (_event: unknown, newPage: number) => {
    setParams(prevState => ({
      ...prevState,
      start_index: (newPage * ROWS_PER_PAGE_DEFAULT).toString()
    }))
  }

  function handleRequestSort(_event: React.MouseEvent<unknown, MouseEvent>, property: string) {
    const isAsc = params.sort_order === SORT_ORDER_ASC
    setParams(prevState => ({
      ...prevState,
      sort_by: property,
      sort_order: `${isAsc ? SORT_ORDER_DESC : SORT_ORDER_ASC}`
    }))
  }

  const handleRowClick = (id: string) => {
    setSelectedRefundCodeId(id)
    setIsDrawerOpen(true)
  }

  const handleDrawerClose = () => {
    setSelectedRefundCodeId('')
    setIsDrawerOpen(false)
  }

  const handleRefundCodeDetailsDrawerTabChange = (
    _event: React.SyntheticEvent,
    newValue: string
  ) => {
    searchParams.set(REFUND_CODE_DETAILS_DRAWER_TAB_PARAM_KEY, newValue)
    setSearchParams(searchParams)
  }

  const handleDelete = () => {
    if (!selectedRefundCodeId) return

    deleteSingleRefundCode(selectedRefundCodeId)
      .unwrap()
      .then(() => {
        handleDrawerClose()
        SnackbarUtils.success(t`Refund code successfully deleted.`)
      })
      .catch(() => {
        SnackbarUtils.error(t`There was an error deleting the refund code.`)
      })
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Breadcrumbs items={breadcrumbs} />
      <Box sx={{ mb: 2 }}>
        <Typography variant="h1" sx={{ mb: 2 }}>{t`Refund codes`}</Typography>
        <Box mt={4}>
          <ListActions>
            <CustomButton
              onClick={() => navigate(`${baseUrl}/create/`)}
              size="small"
              startIcon={<AddIcon />}
            >
              {t`Create new refund code`}
            </CustomButton>
          </ListActions>
          <Box pt={1}>
            <RefundCodeList
              rows={refundCodes}
              isLoading={isRefundCodesFetching || isDeleteLoading}
              onRowClick={handleRowClick}
              selectedRefundCodeId={selectedRefundCodeId}
              params={params}
              handleRequestSort={handleRequestSort}
              handleChangePage={handleChangePage}
              count={totalResults}
              page={
                params.start_index ? Number.parseInt(params.start_index) / ROWS_PER_PAGE_DEFAULT : 0
              }
            />
          </Box>
          <DrawerRight
            open={isDrawerOpen}
            onClose={handleDrawerClose}
            topBar={
              <Box>
                <DeleteButton
                  sx={{ mr: 1 }}
                  size="small"
                  onClick={() =>
                    confirm({
                      title: t`Are you sure?`,
                      message: t`Are you sure you want to delete the refund code?`,
                      handleConfirm: () => {
                        handleDelete()
                      }
                    })
                  }
                >{t`Delete`}</DeleteButton>
                <CustomButton
                  onClick={() => navigate(`${baseUrl}/${selectedRefundCodeId}/edit`)}
                  size="small"
                  startIcon={<EditIcon />}
                >{t`Edit`}</CustomButton>
              </Box>
            }
          >
            <WithLoader loading={isSingleRefundCodeFetching}>
              {singleRefundCode && (
                <TabContext value={refundCodeDetailsDrawerTabFromParams}>
                  <TabList
                    aria-label={t`Refund code tab list`}
                    onChange={handleRefundCodeDetailsDrawerTabChange}
                  >
                    <Tab label={t`Details`} value={RefundCodeTabType.DETAILS} />
                  </TabList>
                  <Divider />
                  <TabPanel sx={{ p: 0 }} value={RefundCodeTabType.DETAILS}>
                    <RefundCodeDetails refundCode={singleRefundCode} />
                  </TabPanel>
                </TabContext>
              )}
            </WithLoader>
          </DrawerRight>
        </Box>
      </Box>
    </Box>
  )
}
